import {yupResolver} from '@hookform/resolvers/yup'
import {FullscreenOverlayWithSpinner} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {type ReactElement, useEffect, useMemo} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {ConfigProvider} from '../../utils/contexts/ConfigContext'
import {LayoutSkeleton} from './components/LayoutSkeleton'
import {ActionProvider} from './contexts/ActionContext'
import {StatusProvider} from './contexts/StatusContext'
import * as classes from './Form.classes'
import {getFormValidationSchema} from './lib/validation'
import type {OrderFormProps, OrderFormState} from './types'
import {renderWidget} from './widgets'

export const OrderForm = ({
  defaultValues,
  layout,
  isLoading,
  isFetching,
  isOverlayVisible,
  onChange,
  config,
  onSubmit,
}: OrderFormProps): ReactElement => {
  const i18n = useI18n()
  const resolver = useMemo(
    () =>
      yupResolver(
        getFormValidationSchema(i18n, config),
        {},
        {
          mode: 'async',
        },
      ),
    [config, i18n],
  )

  const form = useForm<OrderFormState>({
    defaultValues,
    resolver,
    mode: 'onBlur',
  })

  useEffect(() => {
    const watcher = form.watch(data => {
      onChange?.(data)
    })

    return () => {
      watcher.unsubscribe()
    }
  }, [form, onChange])

  if (!isLoading && !layout) {
    throw new Error('Root form widget is not found')
  }

  return (
    <StatusProvider isFetching={isFetching}>
      <ActionProvider onSubmit={onSubmit}>
        <ConfigProvider {...config}>
          <FormProvider {...form}>
            {isOverlayVisible && <FullscreenOverlayWithSpinner />}
            <main className={classes.main}>{layout ? renderWidget(layout) : <LayoutSkeleton />}</main>
          </FormProvider>
        </ConfigProvider>
      </ActionProvider>
    </StatusProvider>
  )
}
